import { Link, useNavigate } from 'react-router-dom';
import { getNaplanHref } from '@/config/naplanStates';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { MapPin, Phone, Mail, Clock, Search, ChevronDown } from 'lucide-react';
import { useLocation } from '@/contexts/LocationContext';
import { useState } from 'react';
import { OPERATING_HOURS } from '@/constants/operatingHours';
import { FOUNDING_YEAR } from '@/constants/company';
import { isCourseFamilyAvailableIn } from '@/config/courseStateMapping';

import { useIsMobile } from '@/hooks/use-mobile';

const Footer = () => {
  const {
    selectedState,
    setSelectedState,
    currentStateConfig,
    stateConfigs
  } = useLocation();
  const navigate = useNavigate();
  const [centerSearch, setCenterSearch] = useState('');
  const isMobile = useIsMobile();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  // Derived from live centre data - never hardcoded
  const statesWithCentres = stateConfigs
    .filter(state => state.centers.length > 0)
    .sort((a, b) => a.code.localeCompare(b.code));
  const hasCentresInSelectedState = (currentStateConfig?.centers.length ?? 0) > 0;

  const formatList = (items: string[]) =>
    items.length <= 1
      ? items.join('')
      : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

  const centreStateNames = formatList(statesWithCentres.map(state => state.name));
  const centreStateCodes = formatList(statesWithCentres.map(state => state.code));

  
  const states = [{
    code: 'NSW',
    name: 'New South Wales'
  }, {
    code: 'VIC',
    name: 'Victoria'
  }, {
    code: 'QLD',
    name: 'Queensland'
  }, {
    code: 'SA',
    name: 'South Australia'
  }, {
    code: 'WA',
    name: 'Western Australia'
  }, {
    code: 'TAS',
    name: 'Tasmania'
  }, {
    code: 'NT',
    name: 'Northern Territory'
  }, {
    code: 'ACT',
    name: 'Australian Capital Territory'
  }];
  
  // Course visibility comes from the shared course-to-state mapping
  const showOc = isCourseFamilyAvailableIn('oc', selectedState);
  const showSelectiveNsw = isCourseFamilyAvailableIn('selective_nsw', selectedState);
  const showSelectiveVic = isCourseFamilyAvailableIn('selective_vic', selectedState);
  const showScholarshipVic = isCourseFamilyAvailableIn('scholarship_vic', selectedState);
  const showGate = isCourseFamilyAvailableIn('gate', selectedState);
  const showNaplan = isCourseFamilyAvailableIn('naplan', selectedState);

  const programsLinks = [
    { name: 'Foundations Course', href: '/programs/foundations' },
    ...(showOc
      ? [
          { name: 'OC TMRW™ Course', href: '/programs/oc-preparation' },
          { name: 'OC to Selective Bridging Course', href: '/programs/tmrw-leap' },
        ]
      : []),
    ...(showSelectiveNsw
      ? [{ name: 'Selective TMRW™ Course', href: '/programs/selective-school' }]
      : []),
    ...(showSelectiveVic
      ? [{ name: 'Victorian Selective Entry Course (VSEC)', href: '/programs/selective-school' }]
      : []),
    ...(showScholarshipVic
      ? [{ name: 'Scholarship Exam Course (SEC)', href: '/programs/scholarship-exam-course' }]
      : []),
    ...(showGate
      ? [{ name: 'ASET / GATE Test Preparation', href: '/courses/aset-gate-test-preparation' }]
      : []),
    ...(showNaplan
      ? [{ name: 'NAPLAN Preparation', href: getNaplanHref(selectedState) }]
      : []),
    ...(selectedState === 'NSW'
      ? [{ name: 'SCOPE™ Exam Event', href: '/scope-exam' }]
      : []),
    { name: 'After-School Course', href: '/programs/afterschool' },
    { name: 'Holiday Course', href: '/programs/term-holiday' },
  ];
  
  const companyLinks = [{
    name: 'Our Story',
    href: '/our-story'
  }, {
    name: 'Our Values',
    href: '/values'
  }, {
    name: 'Executive Team',
    href: '/executive-team'
  }, {
    name: 'Careers',
    href: '/careers'
  }, {
    name: 'Newsroom',
    href: '/newsroom'
  }, {
    name: 'Awards',
    href: '/awards'
  }, {
    name: 'Resources',
    href: '/resources'
  }, {
    name: 'Sitemap',
    href: '/sitemap'
  }];
  
  const legalLinks = [{
    name: 'Contact',
    href: '/contact'
  }, {
    name: 'FAQ',
    href: '/faq'
  }, {
    name: 'Books & Materials',
    href: '/books-materials'
  }, {
    name: 'SchooAI - AI Tutor',
    href: '/schoo-ai'
  }, {
    name: 'Mobile App',
    href: '/mobile-app'
  }, ...(selectedState === 'NSW' ? [{
    name: 'NSW OC Calculator',
    href: '/nsw-oc-calculator'
  }, {
    name: 'NSW Selective Calculator',
    href: '/nsw-selective-calculator'
  }] : []), {
    name: 'Privacy Policy',
    href: '/privacy-policy'
  }, {
    name: 'Request data deletion',
    href: '/data-deletion'
  }, {
    name: 'Terms & Conditions',
    href: '/terms-conditions'
  }, {
    name: 'Partner Access Hub',
    href: '/partner-access-hub'
  }];
  
  const franchiseLinks = [{
    name: 'Schoolola Franchising',
    href: '/franchise'
  }, {
    name: 'Book an Assessment',
    href: '/book-assessment'
  }];
  
  const handleCenterSelect = (value: string) => {
    if (value === 'and-many-more') {
      navigate('/franchise');
    } else {
      navigate(`/centres/${value}`);
    }
  };

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const FooterLinkSection = ({ 
    title, 
    links, 
    sectionKey 
  }: { 
    title: string; 
    links: { name: string; href: string }[]; 
    sectionKey: string;
  }) => {
    const isOpen = openSections[sectionKey] ?? false;

    if (!isMobile) {
      return (
        <div>
          <h3 className="font-semibold text-lg mb-4">{title}</h3>
          <ul className="space-y-2 text-sm">
            {links.map(link => (
              <li key={link.name}>
                <Link to={link.href} className="text-gray-300 hover:text-white hover:underline underline-offset-2 transition-colors">
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      );
    }

    return (
      <Collapsible open={isOpen} onOpenChange={() => toggleSection(sectionKey)}>
        <CollapsibleTrigger className="flex items-center justify-between w-full py-3 border-b border-gray-800">
          <h3 className="font-semibold text-base">{title}</h3>
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="space-y-2 text-sm py-3">
            {links.map(link => (
              <li key={link.name}>
                <Link to={link.href} className="text-gray-300 hover:text-white hover:underline underline-offset-2 transition-colors">
                  {link.name}
                </Link>
              </li>
            ))}
          </ul>
        </CollapsibleContent>
      </Collapsible>
    );
  };
  
  const getCenterOptions = () => {
    if (!currentStateConfig || !currentStateConfig.centers.length) return [];
    const centers = currentStateConfig.centers
      .map(center => ({
        value: center.slug,
        label: center.suburb
      }))
      .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically
    
    return [...centers, {
      value: 'and-many-more',
      label: 'and many more...'
    }];
  };

  const getStateSpecificText = () => {
    if (!hasCentresInSelectedState) {
      // Wait for live centre data before naming the states with centres
      if (statesWithCentres.length === 0) return '';
      return `Schoolola operates centres in ${centreStateNames}. Founded ${FOUNDING_YEAR}.`;
    }

    switch (selectedState) {
      case 'NSW':
        return `Empowering students across New South Wales with expert tutoring in OC TMRW™/Selective TMRW™ preparation, homework assistance, and foundational learning since ${FOUNDING_YEAR}.`;
      case 'VIC':
        return `Empowering students across Victoria with expert tutoring in homework assistance and foundational learning since ${FOUNDING_YEAR}.`;
      case 'WA':
        return `Empowering students across Western Australia with expert tutoring in homework assistance and foundational learning since ${FOUNDING_YEAR}.`;
      default:
        return `Empowering students across ${currentStateConfig?.name ?? 'Australia'} with expert tutoring in homework assistance and foundational learning since ${FOUNDING_YEAR}.`;
    }
  };

  
  return (
    <footer className="bg-gray-900 text-white pt-12 pb-6">
      <div className="container mx-auto px-4">
        {/* Link Columns - Collapsible on Mobile. Courses only where centres operate. */}
        <div className={isMobile ? 'space-y-0 mb-6' : `grid grid-cols-1 md:grid-cols-2 ${hasCentresInSelectedState ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-6 mb-6`}>
          {hasCentresInSelectedState && (
            <FooterLinkSection title="Courses" links={programsLinks} sectionKey="courses" />
          )}
          <FooterLinkSection title="Company" links={companyLinks} sectionKey="company" />
          <FooterLinkSection title="Support" links={legalLinks} sectionKey="support" />
          <FooterLinkSection title="Quick Actions" links={franchiseLinks} sectionKey="actions" />
        </div>


        {/* Combined Location & Contact Section */}
        <div className="border-t border-gray-800 pt-6 mb-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Location Selector */}
            <div>
              <h4 className="font-semibold text-sm mb-2">Location</h4>
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white text-sm h-9">
                  <MapPin className="h-3 w-3 mr-1" />
                  <SelectValue placeholder="Select Location" />
                </SelectTrigger>
                <SelectContent>
                  {states.map(state => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Centre Selector */}
            <div>
              <h4 className="font-semibold text-sm mb-2">Find a Centre</h4>
              {currentStateConfig && currentStateConfig.centers.length > 0 ? (
                <Select onValueChange={handleCenterSelect}>
                  <SelectTrigger className="w-full bg-gray-800 border-gray-700 text-white text-sm h-9">
                    <Search className="h-3 w-3 mr-1" />
                    <SelectValue placeholder="Select Centre" />
                  </SelectTrigger>
                  <SelectContent>
                    {getCenterOptions().map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-xs text-gray-400">No centres yet</p>
              )}

            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-semibold text-sm mb-2">Contact HQ</h4>
              <div className="space-y-1 text-xs text-gray-300">
                <a href="tel:0291617167" className="flex items-center gap-1 hover:text-white hover:underline underline-offset-2 transition-colors">
                  <Phone className="h-3 w-3 text-accent" />
                  <span>(02) 9161 7167</span>
                </a>
                <a href="mailto:hello@schoolola.com.au" className="flex items-center gap-1 hover:text-white hover:underline underline-offset-2 transition-colors">
                  <Mail className="h-3 w-3 text-accent" />
                  <span>hello@schoolola.com.au</span>
                </a>
              </div>
            </div>

            {/* Hours */}
            <div>
              <h4 className="font-semibold text-sm mb-2">Hours</h4>
              <div className="text-xs text-gray-300 space-y-0.5">
                <div>{OPERATING_HOURS.display.weekdays}</div>
                <div>{OPERATING_HOURS.display.saturdays}</div>
                <div>{OPERATING_HOURS.display.sundays}</div>
              </div>
            </div>
          </div>
        </div>

        {/* States with centres - derived from live centre data */}
        {statesWithCentres.length > 0 && (
          <div className="border-t border-gray-800 pt-4 mb-4">
            <div className="flex flex-wrap justify-center items-center gap-2">
              <span className="text-gray-400 text-xs">Serving:</span>
              {statesWithCentres.map((state) => (
                <span key={state.code} className="bg-gray-800 text-gray-300 px-2 py-0.5 rounded text-xs">
                  {state.code}
                </span>
              ))}
              <span className="text-gray-500 text-xs ml-2">• Centres in {centreStateCodes}</span>
            </div>
          </div>
        )}


        {/* Bottom Bar */}
        <div className="border-t border-gray-800 pt-4 text-center">
          <p className="text-gray-300 text-xs leading-relaxed max-w-3xl mx-auto mb-2">{getStateSpecificText()}</p>
          <p className="text-gray-400 text-xs mb-2">
            © {new Date().getFullYear()} Schoolola Tutoring. All rights reserved. | Schoolola PTY LTD ABN 77670324900
          </p>
          <p className="text-gray-500 text-xs">
            <a
              href="/llms.txt"
              className="hover:text-gray-300 hover:underline underline-offset-2 transition-colors inline-flex items-center gap-1"
              aria-label="Machine-readable site index for AI agents and large language models"
            >
              Are you an AI agent? See our llms.txt <span aria-hidden="true">›</span>
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;